import React from "react"
import { Tag } from "antd"
import { SurveyRoutingStep } from "types/surveyTest.types"
import { premiseLines, Segment, stepSegments } from "../model/premise"
import { outcomeOf, Tone } from "../model/routing"
import { classes } from "./ScreenView.component"

/** How each piece of a condition shows: a question named, an option as a small chip, the rest quieter. */
const SEGMENT: Record<Segment["kind"], string> = {
  question: "font-semibold text-gray-900",
  option: "inline-block max-w-full rounded bg-gray-100 px-1.5 align-baseline text-xs leading-5 text-gray-700",
  word: "text-gray-600",
  text: "text-gray-900",
  paren: "text-gray-400",
  connector: "text-xs text-gray-500",
}

/** No space after an opening parenthesis, nor before a closing one, a colon or a comma. */
const joined = (before: Segment, s: Segment): boolean =>
  (before.kind === "paren" && before.text === "(") ||
  (s.kind === "paren" && s.text === ")") ||
  (s.kind === "text" && (s.text === ":" || s.text === ","))

/** Two options in a row: chips show apart, so only screen readers and copied text get the comma between them. */
const listed = (before: Segment, s: Segment): boolean => before.kind === "option" && s.kind === "option"

/** One line of a condition: the first, or one an AND or OR starts. */
const Line: React.FC<{ segments: Segment[] }> = ({ segments }) => (
  <div>
    {segments.map((s, i) => (
      <React.Fragment key={i}>
        {i > 0 && listed(segments[i - 1], s) && <span className="sr-only">,</span>}
        {i > 0 && !joined(segments[i - 1], s) && " "}
        <span className={SEGMENT[s.kind]}>{s.text}</span>
      </React.Fragment>
    ))}
  </div>
)

/** A routing step as readable text: its lead words, the questions it tests, and the options as chips. */
export const RoutingCondition: React.FC<{ step: SurveyRoutingStep }> = ({ step }) => (
  <div className="min-w-0 flex-1 text-sm leading-6 [overflow-wrap:anywhere]">
    {premiseLines(stepSegments(step)).map((line, i) => (
      <Line key={i} segments={line} />
    ))}
  </div>
)

/** The badge colours: a success green, a quiet grey, the danger red, amber. */
const TONE: Record<Tone, string> = {
  yes: "border-green-200 bg-green-50 text-green-600",
  no: "border-gray-200 bg-gray-50 text-gray-500",
  terminated: "border-red-200 bg-red-50 text-red-600",
  skipped: "border-amber-200 bg-amber-50 text-amber-700",
}

/** What a decision came to, in words and colour; nothing for a step without a result. */
export const Outcome: React.FC<{ step: SurveyRoutingStep }> = ({ step }) => {
  const outcome = outcomeOf(step)
  return outcome ? <Tag className={classes("me-0 shrink-0", TONE[outcome.tone])}>{outcome.text}</Tag> : null
}
